import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface Estado {
  erro: Error | null;
}

/**
 * A rede de segurança do painel.
 *
 * Sem isto, qualquer erro de render derruba a árvore inteira e o React deixa a página em
 * branco — foi o que aconteceu com um supervisor no meio de um treinamento: tela preta,
 * nenhuma mensagem, nada no log do servidor (porque o erro era do navegador). Diagnosticar
 * virou adivinhação.
 *
 * Agora a tela diz o que houve, oferece o caminho de volta e mostra o detalhe técnico para
 * quem estiver ajudando — é a diferença entre "travou" e uma linha que aponta o defeito.
 */
export class LimiteDeErro extends Component<Props, Estado> {
  override state: Estado = { erro: null };

  static getDerivedStateFromError(erro: Error): Estado {
    return { erro };
  }

  override componentDidCatch(erro: Error, info: ErrorInfo) {
    // Fica no console do navegador de quem está com o problema: é lá que o suporte olha.
    console.error('[MyMAXX] erro na tela:', erro, info.componentStack);
  }

  override render() {
    const { erro } = this.state;
    if (!erro) return this.props.children;

    return (
      <main className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-4 px-6 py-10">
        <div>
          <h1 className="text-xl font-semibold">Esta tela travou</h1>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
            Não foi você. Recarregue para continuar — se acontecer de novo, mande o texto abaixo
            para quem cuida do sistema.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-marca-600 px-4 py-2 text-sm font-medium text-white hover:bg-marca-700"
          >
            Recarregar
          </button>
          <button
            type="button"
            onClick={() => {
              window.location.href = '/';
            }}
            className="rounded-lg border border-neutral-300 px-4 py-2 text-sm dark:border-neutral-700"
          >
            Voltar ao início
          </button>
        </div>

        <details className="rounded-lg border border-neutral-200 p-3 text-xs dark:border-neutral-800">
          <summary className="cursor-pointer text-neutral-500">Detalhe técnico</summary>
          <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap text-[11px] text-red-600">
            {erro.message}
            {erro.stack ? `\n\n${erro.stack}` : ''}
          </pre>
        </details>
      </main>
    );
  }
}
